import { CSSProperties, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs/Tabs';
import Blade from '@/components/ui/Blade/Blade';
import Alert from '@/components/ui/Alert';
import IconWithText from '@/components/ui/IconWithText/IconWithText';
import TintedIcon from '@/components/ui/TintedIcon/TintedIcon';
import { ManagementSupportedDtoIn, ManagementSupportedClassificationDtoIn } from '@/models/in/ManagementSupportedDtoIn';
import { ManagementSupportedAdditionalInfoDtoIn } from '@/models/in/ManagementSupportedAdditionalInfoDtoIn';
import { BankAccountDtoIn } from '@/models/in/BankAccountDtoIn';
import { ChildBasicDetailsDtoIn } from '@/models/in/ChildBasicDetailsDtoIn';
import { RelationshipStatus } from '@/models/common/RelationshipStatus';
import Fund from '@/models/Fund';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import { getExpirationStatus, getExpirationStatusColor } from '@/helpers/contactClassificationExpiration';
import { getClassificationStatusLabel } from '@/helpers/classificationStatusLabel';
import { formatDateLocale } from '@/helpers/dateHelper';
import {
  ISRAELI_ID_CHANGE_REQUESTS_URL,
  SupportedIsraeliIdChangeRequestDtoIn,
} from '@/models/in/SupportedIsraeliIdChangeRequestDtoIn';
import IsraeliIdChangeRequestHistory from '../IsraeliIdChangeRequest/IsraeliIdChangeRequestHistory';
import userSupport from '@/assets/icons/user-support.svg';
import bank from '@/assets/icons/bank.svg';
import childrenIcon from '@/assets/icons/children.svg';
import paymentIcon from '@/assets/icons/payment-card.svg';
import approvedSupport from '@/assets/icons/approved-support.svg';
import id2Icon from '@/assets/icons/id2.svg';
import calendar from '@/assets/icons/calendar.svg';
import home from '@/assets/icons/home.svg';
import styles from './AdditionalInfo.module.css';

interface AdditionalInfoProps {
  data: ManagementSupportedDtoIn;
  handleXclick: () => void;
  onRefresh: () => void;
  /** Opens the bank accounts dialog for this supported. */
  onEditPaymentInfos: () => void;
  /** Opens the children dialog for this supported. */
  onEditChildren: () => void;
  /** Opens a coordinator's waiting request to correct this supported's ID, to decide it. */
  onReviewIdChangeRequest: (request: SupportedIsraeliIdChangeRequestDtoIn) => void;
}

const AdditionalInfo = ({
  data,
  handleXclick,
  onEditPaymentInfos,
  onEditChildren,
  onReviewIdChangeRequest,
}: AdditionalInfoProps) => {
  const { t, i18n } = useTranslation();

  const { data: additionalInfo } = useGetAuthApi<ManagementSupportedAdditionalInfoDtoIn>(
    `management/supporteds/${data.id}/additional-info`
  );

  const { data: paymentInfos, isLoading: isLoadingPaymentInfos } = useGetAuthApi<BankAccountDtoIn[]>(
    `management/supporteds/${data.id}/payment-infos`
  );

  const { data: children, isLoading: isLoadingChildren } = useGetAuthApi<ChildBasicDetailsDtoIn[]>(
    `management/supporteds/${data.id}/children`
  );

  // HAT-1428: a coordinator's request to correct the ID is decided on the supported it is about.
  // Under the board's own key, so a decision there refreshes this too.
  const { data: idChangeRequests } = useGetAuthApi<SupportedIsraeliIdChangeRequestDtoIn[]>(
    `${ISRAELI_ID_CHANGE_REQUESTS_URL}?contactId=${data.id}`
  );
  const pendingIdChange = idChangeRequests?.find((r) => r.status === 'Pending');
  const decidedIdChanges = idChangeRequests?.filter((r) => r.status !== 'Pending') ?? [];

  // The funds list is already cached by the fund filter, so naming the funds costs no extra call
  const { data: funds } = useGetAuthApi<Fund[]>('funds');

  const fundName = (fundId: number) => funds?.find((fund) => fund.id === fundId)?.name;

  const fundLabel = (fundId: number) => {
    const name = fundName(fundId);
    return name ? `${fundId} | ${name}` : String(fundId);
  };

  const addressText = () => {
    const address = additionalInfo?.address;
    if (!address) return '';

    return [
      [address.streetName, address.streetNumber].filter(Boolean).join(' '),
      address.apartmentNumber ? `${t('apartment')} ${address.apartmentNumber}` : '',
      address.city,
      address.zipCode,
    ].filter(Boolean).join(', ');
  };

  const infoRow = (label: string, value: ReactNode) => (
    <div className={styles.infoRow}>
      <span className={`body-sm-regular ${styles.infoLabel}`}>{label}:</span>
      <span className={`body-sm-medium ${styles.infoValue}`}>{value}</span>
    </div>
  );

  const section = (title: ReactNode, children: ReactNode) => (
    <div className={styles.infoSection}>
      <h3 className="body-md-semibold text-(--font-sub-title)">{title}</h3>
      {children}
    </div>
  );

  /** A section title with the icon button that opens the matching dialog. */
  const sectionTitleWithAction = (title: string, icon: string, onClick: () => void) => (
    <div className="flex items-center gap-2">
      <span>{title}</span>
      <button
        type="button"
        title={title}
        aria-label={title}
        className={styles.iconAction}
        onClick={onClick}
      >
        <TintedIcon icon={icon} size={20} />
      </button>
    </div>
  );

  const headerTop = (
    <div className="flex items-center gap-3">
      <img src={userSupport} alt="" className="w-6 h-6" />
      <div className="flex flex-col">
        <span className="body-lr-semibold text-(--font-body-4)">
          {data.firstName} {data.lastName}
        </span>
        <span className="body-sm-regular text-(--font-body-4)">
          {data.israeliId}
        </span>
      </div>
    </div>
  );

  // Only shown when there is something to show, so a supported without classifications doesn't get
  // an empty band under the name.
  const headerBottom = data.contactClassifications.length === 0 ? <></> : (
    <div className="flex flex-wrap gap-2 min-w-0">
      {data.contactClassifications.map((classification) => (
        <span key={classification.id} className={`${styles.classification} body-md-medium`}>
          <span
            className={styles['classification-color']}
            style={{ '--classification-color': getExpirationStatusColor(getExpirationStatus(classification)) } as CSSProperties}
          ></span>
          {t(classification.name)}
        </span>
      ))}
    </div>
  );

  /** One classification, with everything known about it in this view. */
  const classificationDetails = (classification: ManagementSupportedClassificationDtoIn) => {
    const expirationStatus = getExpirationStatus(classification);

    return (
      <div key={classification.id} className={styles.card}>
        <div className="flex items-center gap-2">
          <span
            className={styles['classification-color']}
            style={{ '--classification-color': getExpirationStatusColor(expirationStatus) } as CSSProperties}
          ></span>
          <span className="body-lr-semibold text-(--font-body-1)">{t(classification.name)}</span>
        </div>
        {infoRow(t('status'), getClassificationStatusLabel(classification.status, t))}
        {infoRow(
          t('classificationExpiration'),
          classification.expiresTimestamp
            ? `${formatDateLocale(classification.expiresTimestamp, i18n.language)}${expirationStatus ? ` (${t(`filter.${expirationStatus}`)})` : ''}`
            : '—'
        )}
        {infoRow(t('requestDate'), formatDateLocale(classification.requestedAt, i18n.language))}
        {classification.approvedAt && infoRow(t('approvalDate'), formatDateLocale(classification.approvedAt, i18n.language))}
      </div>
    );
  };

  // Classifications belong to a fund, so they're grouped by fund - one block per fund the supported
  // is registered in, including funds with no classification yet.
  const classificationsByFund = data.fundIds.map((fundId) => ({
    fundId,
    classifications: data.contactClassifications.filter((classification) => classification.fundId === fundId),
  }));

  return (
    <Blade handleXclick={handleXclick} headerTop={headerTop} headerBottom={headerBottom} width="560px">
      {pendingIdChange && (
        <Alert
          type="warning"
          title={t('israeliIdChangeRequests.blade.pending', { id: pendingIdChange.requestedIsraeliId })}
          body={t('israeliIdChangeRequests.requestedBy', {
            name: pendingIdChange.requestedByName || pendingIdChange.requestedByEmail || '-',
            fund: pendingIdChange.fundName ? `${pendingIdChange.fundName} (${pendingIdChange.fundId})` : pendingIdChange.fundId,
            date: formatDateLocale(pendingIdChange.requestedAt, i18n.language),
          })}
          action={{ label: t('israeliIdChangeRequests.blade.review'), onClick: () => onReviewIdChangeRequest(pendingIdChange) }}
          dismissible={false}
          className={styles.idChangeNotice}
        />
      )}
      <Tabs defaultValue="details" style={{ direction: 'inherit' }}>
        <TabsList>
          <TabsTrigger value="details">{t('supportedsManagement.blade.details')}</TabsTrigger>
          <TabsTrigger value="classifications">{t('supportedsManagement.blade.classificationsAndFunds')}</TabsTrigger>
        </TabsList>

        {/* Personal details, contact info, address, bank accounts and children */}
        <TabsContent value="details" className={`${styles.content} custom-scrollbar`}>
          {section(t('supportedsManagement.blade.personalDetails'), (
            <>
              {infoRow(t('fullName'), `${data.firstName} ${data.lastName}`)}
              {infoRow(t('israeliId'), data.israeliId)}
              {additionalInfo?.birthDate && infoRow(t('birthDate'), formatDateLocale(additionalInfo.birthDate, i18n.language))}
              {infoRow(t('phone'), data.phone || '—')}
              {infoRow(t('email'), data.email || '—')}
              {infoRow(t('address'), addressText() || '—')}
            </>
          ))}

          {decidedIdChanges.length > 0 && (
            <>
              <div className={styles.line} />
              {section(t('israeliIdChangeRequests.blade.history'), <IsraeliIdChangeRequestHistory requests={decidedIdChanges} />)}
            </>
          )}

          {additionalInfo?.spouse && (
            <>
              <div className={styles.line} />
              {section(t('spouseDetails'), (
                <>
                  {infoRow(t('fullName'), `${additionalInfo.spouse.firstName} ${additionalInfo.spouse.lastName}`)}
                  {infoRow(t('israeliId'), additionalInfo.spouse.israeliId)}
                  {additionalInfo.spouse.phone && infoRow(t('phone'), additionalInfo.spouse.phone)}
                  {additionalInfo.spouse.email && infoRow(t('email'), additionalInfo.spouse.email)}
                </>
              ))}
            </>
          )}

          <div className={styles.line} />

          {/* Payment Details Section */}
          {section(sectionTitleWithAction(t('paymentDetails'), paymentIcon, onEditPaymentInfos), (
            isLoadingPaymentInfos ? (
              <p className={styles.loading}>{t('loading')}...</p>
            ) : paymentInfos && paymentInfos.length > 0 ? (
              <div className="flex flex-col gap-3">
                {paymentInfos.map((paymentInfo, index) => (
                  <div key={index} className="flex gap-2 items-start pt-0.5 pb-0.5">
                    <img src={bank} alt="" className="p-0.5" />
                    <div className="flex flex-col">
                      <span className="body-lr-regular text-(--font-body-1)">{paymentInfo.accountOwnerName}</span>
                      <span className="body-lr-regular text-(--font-body-1)">{paymentInfo.accountNumber}/{paymentInfo.branchNumber}</span>
                      <span className="body-lr-regular text-(--font-body-1)">{paymentInfo.bankName} {paymentInfo.bankNumber}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className={styles.noData}>{t('noBankAccountsFound')}</p>
            )
          ))}

          <div className={styles.line} />

          {/* Children Section */}
          {section(sectionTitleWithAction(t('children'), childrenIcon, onEditChildren), (
            isLoadingChildren ? (
              <p className={styles.loading}>{t('loading')}...</p>
            ) : children && children.length > 0 ? (
              <div className="flex flex-col gap-4">
                {children.map((child) => (
                  <div key={child.id} className="flex flex-col gap-2">
                    <div className="flex gap-2 items-center">
                      <div className="h-[15px] border-l-2 border-(--border-tertiary-strong)"></div>
                      {child.status === RelationshipStatus.Approved && (
                        <img src={approvedSupport} alt="" className="w-5 h-5" />
                      )}
                      <span className="body-lr-semibold text-(--font-body-1)">
                        {child.firstName} {child.lastName}
                      </span>
                    </div>
                    <div className="flex flex-col">
                      {child.status !== RelationshipStatus.Approved && (
                        <span className="body-lr-regular text-(--font-body-1)">{t(`childStatus.${child.status}`)}</span>
                      )}
                      <IconWithText icon={id2Icon} text={child.israeliId} />
                      {child.birthDate && (
                        <IconWithText icon={calendar} text={formatDateLocale(child.birthDate, i18n.language)} />
                      )}
                      {child.livesWithSupported && (
                        <IconWithText icon={home} text={t('livesWithSupported')} />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className={styles.noData}>{t('noChildrenYet')}</p>
            )
          ))}
        </TabsContent>

        {/* Classifications, grouped by the fund they belong to */}
        <TabsContent value="classifications" className={`${styles.content} custom-scrollbar`}>
          {section(t('funds'), (
            <div className={styles.fundsList}>
              {data.fundIds.map((fundId) => (
                <span key={fundId} className={`${styles.fundBadge} body-sm-regular`}>{fundLabel(fundId)}</span>
              ))}
            </div>
          ))}

          <div className={styles.line} />

          {classificationsByFund.map(({ fundId, classifications }, index) => (
            <div key={fundId} className="flex flex-col gap-4">
              {index > 0 && <div className={styles.line} />}
              {section(fundLabel(fundId), (
                classifications.length > 0 ? (
                  <div className="flex flex-col gap-3">
                    {classifications.map(classificationDetails)}
                  </div>
                ) : (
                  <p className={styles.noData}>{t('noClassifications')}</p>
                )
              ))}
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </Blade>
  );
};

export default AdditionalInfo;
